import React, { useEffect } from 'react';
import { OrphanedSessionModal } from '@/components/session/OrphanedSessionModal';
import { useOrphanedSession } from '@/hooks/useOrphanedSession';
import { useAuth } from '@/hooks/useAuth';
import { useTimerContext } from '@/contexts/TimerContext';

interface OrphanedSessionHandlerProps {
  children: React.ReactNode;
}

export function OrphanedSessionHandler({ children }: OrphanedSessionHandlerProps) {
  const { user, initialized } = useAuth();
  const { orphanedSession, isChecking, checkForOrphanedSession, clearOrphanedSession, restoreOrphanedSession } = useOrphanedSession();
  const { restoreTimerWithServerTime } = useTimerContext();

  // Check for orphaned sessions when user is authenticated and app is initialized
  useEffect(() => {
    if (initialized && user && !isChecking) {
      checkForOrphanedSession(user.id);
    }
  }, [initialized, user, isChecking, checkForOrphanedSession]);

  const handleContinueSession = async (session: any) => {
    try {
      console.log('Continuing orphaned session:', session.id);
      
      // Create a timer state from the orphaned session
      const timerState = {
        sessionId: session.id,
        totalDuration: session.duration * 60, // Convert minutes to seconds
        startTime: session.started_at ? new Date(session.started_at).getTime() : Date.now(),
        pauseTime: session.paused_at ? new Date(session.paused_at).getTime() : null,
        isRunning: session.status === 'active',
        isPaused: session.status === 'paused',
        elapsedTime: 0, // Will be calculated by PreciseTimer
        remainingTime: session.duration * 60,
        progress: 0,
        formattedTime: '00:00',
        lastUpdate: Date.now()
      };

      // Restore the timer state
      restoreTimerWithServerTime(session.id, timerState);
      
      // Mark as restored
      restoreOrphanedSession(session);
      
    } catch (error) {
      console.error('Failed to continue orphaned session:', error);
      // Clear the orphaned session on error
      clearOrphanedSession();
    }
  };

  const handleDiscardSession = () => {
    console.log('Discarding orphaned session');
    clearOrphanedSession();
  };

  return (
    <>
      {children}
      <OrphanedSessionModal
        isOpen={!!orphanedSession}
        onClose={() => clearOrphanedSession()}
        orphanedSession={orphanedSession}
        onContinueSession={handleContinueSession}
        onDiscardSession={handleDiscardSession}
      />
    </>
  );
}

export default OrphanedSessionHandler;
